/**
 * Restaurant menu (MVP 1).
 *
 * A place can point at its menu in three ways — a link to an existing menu
 * page, an uploaded PDF, or an uploaded image. All three are stored on the
 * `places` row (`menu_external_url`, `menu_pdf_url`, `menu_image_url`) and are
 * rendered server-side on the public place page so Google and AI crawlers see
 * the menu in the first HTML response instead of after a client fetch.
 */
import { normalizeWebUrl } from "@/lib/normalize-url";

export type PlaceMenu = {
  url: string | null;
  pdf: string | null;
  image: string | null;
  updatedAt: string | null;
};

export const EMPTY_PLACE_MENU: PlaceMenu = { url: null, pdf: null, image: null, updatedAt: null };

/** Build the menu DTO from a raw `places` row. */
export function placeMenuFromRow(row: Record<string, any> | null | undefined): PlaceMenu {
  if (!row) return EMPTY_PLACE_MENU;
  return {
    url: (row.menu_external_url as string) || null,
    pdf: (row.menu_pdf_url as string) || null,
    image: (row.menu_image_url as string) || null,
    updatedAt: (row.menu_updated_at as string) || null,
  };
}

export function hasPlaceMenu(menu: PlaceMenu | null | undefined): boolean {
  return Boolean(menu && (menu.url || menu.pdf || menu.image));
}

/** Best single canonical URL for the menu (used by Schema.org `hasMenu`). */
export function primaryMenuUrl(menu: PlaceMenu | null | undefined): string | null {
  if (!menu) return null;
  return menu.url || menu.pdf || menu.image || null;
}

/** Sanitise partner-entered menu values before they are stored. */
export function normalizeMenuInput(input: {
  url?: string | null;
  pdf?: string | null;
  image?: string | null;
}): { menu_external_url: string | null; menu_pdf_url: string | null; menu_image_url: string | null } {
  return {
    menu_external_url: normalizeWebUrl(input.url ?? null),
    menu_pdf_url: normalizeWebUrl(input.pdf ?? null),
    menu_image_url: normalizeWebUrl(input.image ?? null),
  };
}

/**
 * Schema.org `Menu` node for a restaurant page: the menu link/PDF/photo plus
 * any dish-level items entered in the admin menu editor.
 */
export function buildMenuJsonLd(opts: {
  menu: PlaceMenu;
  placeName: string;
  placeUrl: string;
  dishes?: MenuDish[];
}): Record<string, unknown> | null {
  const url = primaryMenuUrl(opts.menu);
  const sections = buildMenuSectionsJsonLd(opts.dishes ?? []);
  if (!url && sections.length === 0) return null;
  return {
    "@type": "Menu",
    "@id": `${opts.placeUrl}#menu`,
    name: `${opts.placeName} menu`,
    ...(url ? { url } : { url: `${opts.placeUrl}#menu` }),
    ...(opts.menu.image ? { image: opts.menu.image } : {}),
    ...(opts.menu.updatedAt ? { dateModified: opts.menu.updatedAt } : {}),
    ...(sections.length ? { hasMenuSection: sections } : {}),
    inLanguage: ["fi", "en"],
  };
}


/* ------------------------------------------------------------------ *
 * MVP 2 — dish-level menu items (`place_menu_items`)
 * ------------------------------------------------------------------ */

export type MenuDish = {
  id: string;
  section: string | null;
  name: string;
  description: string | null;
  price: number | null;
  currency: string;
  dietaryTags: string[];
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
};

export function menuDishFromRow(row: Record<string, any>): MenuDish {
  return {
    id: String(row.id),
    section: (row.section as string) || null,
    name: (row.name as string) ?? "",
    description: (row.description as string) || null,
    price: row.price === null || row.price === undefined ? null : Number(row.price),
    currency: (row.currency as string) || "EUR",
    dietaryTags: Array.isArray(row.dietary_tags) ? (row.dietary_tags as string[]) : [],
    imageUrl: (row.image_url as string) || null,
    sortOrder: Number(row.sort_order ?? 0),
    isActive: row.is_active !== false,
  };
}


/**
 * Swap dish name/description/section for their stored translations.
 * `map` is keyed `${dishId}:${field}` (see `fetchManyEntityTranslations`).
 * Anything without a translation keeps the original English text.
 */
export function applyDishTranslations(dishes: MenuDish[], map: Record<string, string>): MenuDish[] {
  if (!map || Object.keys(map).length === 0) return dishes;
  return dishes.map((d) => ({
    ...d,
    name: map[`${d.id}:name`] ?? d.name,
    description: map[`${d.id}:description`] ?? d.description,
    section: map[`${d.id}:section`] ?? d.section,
  }));
}

/** Group dishes into ordered menu sections (unnamed section first). */
export function groupMenuDishes(dishes: MenuDish[]): { section: string; dishes: MenuDish[] }[] {
  const groups = new Map<string, MenuDish[]>();
  for (const d of [...dishes].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const key = (d.section ?? "").trim();
    const list = groups.get(key);
    if (list) list.push(d);
    else groups.set(key, [d]);
  }
  return [...groups.entries()].map(([section, list]) => ({ section, dishes: list }));
}

export function formatDishPrice(price: number | null, currency = "EUR", lang: "en" | "fi" = "en"): string | null {
  if (price === null || Number.isNaN(price)) return null;
  try {
    return new Intl.NumberFormat(lang === "fi" ? "fi-FI" : "en-GB", {
      style: "currency",
      currency: currency || "EUR",
      minimumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${price.toFixed(2)} ${currency}`;
  }
}

/** Schema.org `hasMenuSection` / `MenuItem` nodes built from dish rows. */
export function buildMenuSectionsJsonLd(dishes: MenuDish[]): Record<string, unknown>[] {
  return groupMenuDishes(dishes.filter((d) => d.isActive && d.name.trim())).map((group) => ({
    "@type": "MenuSection",
    ...(group.section ? { name: group.section } : {}),
    hasMenuItem: group.dishes.map((d) => ({
      "@type": "MenuItem",
      name: d.name,
      ...(d.description ? { description: d.description } : {}),
      ...(d.imageUrl ? { image: d.imageUrl } : {}),
      ...(d.dietaryTags.length ? { suitableForDiet: d.dietaryTags } : {}),
      ...(d.price !== null
        ? { offers: { "@type": "Offer", price: d.price.toFixed(2), priceCurrency: d.currency || "EUR" } }
        : {}),
    })),
  }));
}


/* ------------------------------------------------------------------ *
 * Dietary badges (veg / non-veg / vegan / gluten-free …)
 * ------------------------------------------------------------------ */

export type DietPreset = { key: string; en: string; fi: string; tone: "veg" | "nonveg" | "info" };

/** Quick-pick dietary presets offered in the menu editor. */
export const DIET_PRESETS: DietPreset[] = [
  { key: "vegetarian", en: "Vegetarian", fi: "Kasvis", tone: "veg" },
  { key: "vegan", en: "Vegan", fi: "Vegaani", tone: "veg" },
  { key: "non-vegetarian", en: "Non-veg", fi: "Liha", tone: "nonveg" },
  { key: "contains-fish", en: "Fish", fi: "Kala", tone: "nonveg" },
  { key: "gluten-free", en: "Gluten-free", fi: "Gluteeniton", tone: "info" },
  { key: "lactose-free", en: "Lactose-free", fi: "Laktoositon", tone: "info" },
  { key: "spicy", en: "Spicy", fi: "Tulinen", tone: "info" },
];

/** Human label + colour tone for one stored dietary tag. */
export function dietBadge(tag: string, lang: "en" | "fi" = "en"): { label: string; tone: DietPreset["tone"] } {
  const key = tag.trim().toLowerCase();
  const preset = DIET_PRESETS.find((p) => p.key === key || p.en.toLowerCase() === key || p.fi.toLowerCase() === key);
  if (preset) return { label: lang === "fi" ? preset.fi : preset.en, tone: preset.tone };
  return { label: tag.trim(), tone: "info" };
}
